import { TableCell } from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, isAfter } from "date-fns";
import { PaymentStatus } from "@/hooks/admin/useUserPayments";
import { DebtInfo } from "@/hooks/admin/useMemberDebts";

interface UserPaymentStatusProps {
  userId: string;
  paymentStatus?: PaymentStatus;
  debtInfo?: DebtInfo;
}

export const UserPaymentStatus = ({ userId, paymentStatus, debtInfo }: UserPaymentStatusProps) => {
  const { data: lastPayment, isLoading } = useQuery({
    queryKey: ["lastPayment", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("*")
        .eq("user_id", userId)
        .or("is_article_sale.is.null,is_article_sale.eq.false")
        .order("paid_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: paymentStatus === undefined,
  });

  // Debt overrides everything
  if (debtInfo) {
    const overdue = debtInfo.visualStatus === "overdue";
    return (
      <TableCell>
        <span
          className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
            overdue
              ? "bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40"
              : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40"
          }`}
        >
          {overdue ? "En deuda VENCIDO" : "En deuda"}
        </span>
      </TableCell>
    );
  }

  if (paymentStatus !== undefined) {
    const label = paymentStatus === "upToDate" ? "Al día" : paymentStatus === "expired" ? "Vencida" : "Sin pagos";
    const colorClass = paymentStatus === "upToDate"
      ? "bg-green-500/10 text-green-500"
      : "bg-red-500/10 text-red-500";
    return (
      <TableCell>
        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${colorClass}`}>
          {label}
        </span>
      </TableCell>
    );
  }

  if (isLoading) return <TableCell className="text-center">Cargando...</TableCell>;

  if (!lastPayment) {
    return (
      <TableCell>
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-500">
          Sin pagos
        </span>
      </TableCell>
    );
  }

  const paymentExpirationDate = addDays(new Date(lastPayment.paid_at), lastPayment.validity_days);
  const isUpToDate = isAfter(paymentExpirationDate, new Date());

  return (
    <TableCell>
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
          isUpToDate ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
        }`}
      >
        {isUpToDate ? "Al día" : "Vencida"}
      </span>
    </TableCell>
  );
};
